import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import { useTextDomainContext } from "@/app/providers";
import { useCountries, useRegister, useTimezones } from "@/hooks";
import { useSearchParams } from "react-router";
import { getGeoInfo, getPartnerKey } from "@/utils";
import detectBrowserLanguage from "detect-browser-language";
import { PasswordInput, PasswordValidator } from "@/components";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const { VITE_RECAPTCHA_SITE_KEY } = import.meta.env;

/**
 * Register Form
 * @returns {Element}
 * @constructor
 */
export const RegistrationForm = () => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { countries } = useCountries();
  const { timezones } = useTimezones();
  const partnerKey = getPartnerKey();

  // reCAPTCHA site key comes from environment var VITE_RECAPTCHA_SITE_KEY
  // When absent, captcha is skipped to keep existing behaviour.
  const siteKey = VITE_RECAPTCHA_SITE_KEY;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [inputCountry, setInputCountry] = useState("DK");
  const [inputTimezone, setInputTimezone] = useState("Europe/Copenhagen");
  const [infoMessage, setInfoMessage] = useState([]);
  const [errorMessage, setErrorMessage] = useState([]);
  const [passwordValid, setPasswordValid] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);

  // Use reCAPTCHA v3 hook - must be called unconditionally at top level
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    getGeoInfo().then((ipData) => {
      const country = countries.find((country) => {
        return country?.code === ipData?.country_code;
      });

      if (country?.code) {
        setInputCountry(country.code);
      } else {
        setInputCountry("DK");
      }

      const timezone = timezones.find((timezone: { zoneName?: string }) => {
        return timezone?.zoneName === ipData?.timezone;
      });

      if (timezone?.zoneName) {
        setInputTimezone(timezone.zoneName);
      } else {
        setInputTimezone("Europe/Copenhagen");
      }
    });
  }, [countries, timezones]);

  // Compute saveButtonDisabled based on form validation
  // Also disable if reCAPTCHA is configured but not ready yet
  const saveButtonDisabled = !(
    name &&
    email &&
    password &&
    confirmPassword &&
    passwordValid &&
    confirmPassword === password &&
    (!siteKey || executeRecaptcha) // If siteKey is set, wait for executeRecaptcha to be ready
  );

  const { submit, isSubmitting } = useRegister(
    () => {
      setErrorMessage([]);
      setInfoMessage([gettext("Registered successful. Redirecting.")]);
    },
    (error) => {
      setInfoMessage([]);
      setErrorMessage([error]);
    },
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage([]);
    const countryCode = inputCountry || "DK";
    const timezone = inputTimezone || "Europe/Copenhagen";
    const lang = detectBrowserLanguage()?.split("-")[0] || "da";

    let captcha = null;
    if (siteKey && executeRecaptcha) {
      try {
        captcha = await executeRecaptcha("register");
      } catch {
        setErrorMessage([
          gettext("CAPTCHA verification failed. Please try again."),
        ]);
        return;
      }
    } else if (siteKey && !executeRecaptcha) {
      // This shouldn't happen because the button is disabled, but just in case
      setInfoMessage([
        gettext("Please wait while security verification loads..."),
      ]);
      return;
    }

    if (!errorMessage?.length) {
      setInfoMessage([gettext("Saving. Please wait.")]);
      const payload: any = {
        name,
        email,
        password,
        partnerKey,
        countryCode,
        timezone,
        lang,
        customerType: "company",
        status: "Active",
        token,
        host: window.location.origin,
      };

      if (captcha) {
        payload.captcha = captcha;
      }

      submit(payload);
    }
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    if (event.target.value) {
      setNameError(false);
    } else {
      setNameError(true);
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    if (event.target.value) {
      setEmailError(false);
    } else {
      setEmailError(true);
    }
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    if (confirmPassword && confirmPassword !== event.target.value) {
      setConfirmPasswordError(true);
    } else {
      setConfirmPasswordError(false);
    }
  };

  const handlePasswordIsValid = (isValid: boolean) => {
    setPasswordValid(isValid);
  };

  const handleConfirmPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setConfirmPassword(event.target.value);
    if (password !== event.target.value) {
      setConfirmPasswordError(true);
    } else {
      setConfirmPasswordError(false);
    }
  };

  return (
    <Box component="form" method="post" onSubmit={handleSubmit}>
      <Grid
        container
        spacing={2}
        sx={{ justifyContent: "center", alignItems: "center" }}
      >
        <Grid size={{ xs: 12, sm: 8 }}>
          <Typography variant="h2" sx={{ color: "grey.900" }}>
            {gettext("Sign up")}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, sm: 8 }}>
          <FormControl fullWidth>
            <OutlinedInput
              id="name-signup"
              type="text"
              size="medium"
              value={name}
              name="name"
              placeholder={gettext("Enter full name")}
              onChange={handleNameChange}
              fullWidth
              required
            />
          </FormControl>

          {nameError && (
            <FormHelperText error id="accountId-error">
              {gettext("Name is required.")}
            </FormHelperText>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <Stack>
            <OutlinedInput
              fullWidth
              size="medium"
              id="email-signup"
              type="email"
              placeholder={gettext("Enter email address")}
              value={email}
              name="email"
              onChange={handleEmailChange}
              inputProps={{}}
              required
            />
          </Stack>
          {emailError && (
            <FormHelperText error id="accountId-error">
              {gettext("Email is required.")}
            </FormHelperText>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <Stack>
            <PasswordInput
              placeholder={gettext("Password")}
              value={password}
              required
              onChange={handlePasswordChange}
            />
          </Stack>
          <PasswordValidator
            password={password}
            handleIsValid={handlePasswordIsValid}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <Stack>
            <PasswordInput
              placeholder={gettext("Confirm Password")}
              value={confirmPassword}
              required
              onChange={handleConfirmPasswordChange}
            />
          </Stack>
          {confirmPasswordError && (
            <FormHelperText error id="accountId-error">
              {gettext("Confirm password does not match.")}
            </FormHelperText>
          )}
        </Grid>

        {siteKey && !executeRecaptcha && (
          <Grid size={{ xs: 12, sm: 8 }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              {gettext("Loading security verification...")}
            </Typography>
          </Grid>
        )}

        <Grid size={{ xs: 12, sm: 8 }}>
          <Button
            disableElevation
            disabled={isSubmitting || saveButtonDisabled}
            fullWidth
            size="medium"
            type="submit"
            variant="contained"
            sx={{ color: "primary" }}
          >
            {gettext("Sign up")}
          </Button>
        </Grid>

        {(infoMessage?.length ?? 0) > 0 && (
          <Grid size={{ xs: 12, sm: 8 }}>
            <Alert
              severity="info"
              sx={{ whiteSpace: "pre-line", textAlign: "left" }}
            >
              {infoMessage.join("\n")}
            </Alert>
          </Grid>
        )}
        {(errorMessage?.length ?? 0) > 0 && (
          <Grid size={{ xs: 12, sm: 8 }}>
            <Alert
              severity="error"
              sx={{ whiteSpace: "pre-line", textAlign: "left" }}
            >
              {errorMessage.join("\n")}
            </Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
